import { forwardRef, type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { clsx } from 'clsx';
import type { ToastSeverity } from '@shared/types';
import { Button } from './Button';

const toastVariants = cva(
  'pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-lg border p-4 shadow-lg transition-all duration-300',
  {
    variants: {
      severity: {
        success:
          'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400',
        error:
          'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400',
        warning:
          'border-yellow-200 bg-yellow-50 text-yellow-800 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
        info: 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
      },
    },
    defaultVariants: {
      severity: 'info',
    },
  }
);

const iconMap: Record<ToastSeverity, string> = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
};

export interface ToastProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'id'>,
    VariantProps<typeof toastVariants> {
  message: string;
  severity?: ToastSeverity;
  actionLabel?: string;
  onAction?: () => void;
  onClose: () => void;
}

export const Toast = forwardRef<HTMLDivElement, ToastProps>(
  (
    {
      className,
      message,
      severity = 'info',
      actionLabel,
      onAction,
      onClose,
      ...props
    },
    ref
  ) => {
    const handleAction = () => {
      onAction?.();
      onClose();
    };

    return (
      <div
        ref={ref}
        role="alert"
        aria-live="polite"
        className={clsx(toastVariants({ severity }), className)}
        {...props}
      >
        <span
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-current/10 text-sm"
          aria-hidden="true"
        >
          {iconMap[severity]}
        </span>

        <p className="flex-1 text-sm font-medium">{message}</p>

        <div className="flex shrink-0 items-center gap-2">
          {actionLabel && onAction && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAction}
              className="h-auto px-2 py-1 text-xs font-medium underline-offset-2 hover:underline"
            >
              {actionLabel}
            </Button>
          )}

          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-current focus:ring-offset-2"
            aria-label="Cerrar notificación"
          >
            <span aria-hidden="true" className="text-lg leading-none">
              ×
            </span>
          </button>
        </div>
      </div>
    );
  }
);

Toast.displayName = 'Toast';
